import { useTranslation } from "react-i18next"

import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { cn } from "@/lib/utils"
import type {
  AssignmentMode,
  AssignmentModeOption,
} from "@/types/mission-assign"

/**
 * MissionAssignModePicker — three-card radio group for picking the
 * assignment mode (Entire crew / Segment / Hand-pick). UX Spec §1.
 *
 * Sibling component to `mission-type-selector.tsx` (OKS-29) — same
 * visual idiom, different domain. Per UX Spec §8 we ship a sibling
 * component first; refactor to a shared primitive once a third site
 * shows up.
 *
 * **A11y:**
 *   - Real `<RadioGroup>` (radix) — arrow-key navigable; `aria-checked`
 *     reflects state; Tab focus on the selected card.
 *   - `data-selected="true|false"` is the canonical visual cue (color
 *     paired with state attr, never color-alone).
 *
 * **Wire shape:** parent owns the URL via `useAssignmentUrl`. This
 * component just emits `onChange(next)` with the picked mode.
 */
interface MissionAssignModePickerProps {
  /** Current selection — null when no mode picked. */
  value: AssignmentMode | null
  /** Called with the new selection. Parent owns the mode-switch dialog. */
  onChange: (next: AssignmentMode) => void
  /** Mode options + helper copy + optional badge_count (e.g. crew size). */
  modeOptions: AssignmentModeOption[]
  /** Disabled when crew is empty / mission is draft. */
  disabled?: boolean
  /** Test ID for the wrapper. */
  testId?: string
}

export function MissionAssignModePicker({
  value,
  onChange,
  modeOptions,
  disabled = false,
  testId = "mission-assign-mode-picker",
}: MissionAssignModePickerProps) {
  const { t } = useTranslation()

  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <fieldset
        aria-disabled={disabled || undefined}
        aria-label={t("missions.assign.mode_picker.legend")}
        className="flex flex-col gap-3"
      >
        <legend className="text-foreground text-sm font-medium">
          {t("missions.assign.mode_picker.legend")}
        </legend>
        <p className="text-muted-foreground text-xs">
          {t("missions.assign.mode_picker.helper")}
        </p>

        <RadioGroup
          name="mode"
          value={value ?? undefined}
          onValueChange={(next) => {
            if (disabled) return
            onChange(next as AssignmentMode)
          }}
          disabled={disabled}
          // OKS-1042 — a 4th card (« Inscrire pour le créateur ») joins on
          // drops that offer a product, so the grid wraps 2-up at `md` and
          // 4-up at `lg` rather than 3-up (UX-Spec §2.1 / §5).
          className="grid gap-3 md:grid-cols-2 lg:grid-cols-4"
        >
          {modeOptions.map((option) => (
            <ModeCard
              key={option.value}
              option={option}
              selected={value === option.value}
              disabled={disabled}
            />
          ))}
        </RadioGroup>
      </fieldset>
    </div>
  )
}

interface ModeCardProps {
  option: AssignmentModeOption
  selected: boolean
  disabled: boolean
}

function ModeCard({ option, selected, disabled }: ModeCardProps) {
  const { t } = useTranslation()
  const id = `mission-assign-mode-${option.value}`

  // OKS-958 — the badge count, labelled and reachable.
  //
  // `typeof === "number"` deliberately admits 0: an empty crew renders the
  // card with a real "no creators" count, which is decision-relevant (it is
  // why "Toute la crew" is not the mode you want). Do NOT narrow this to a
  // truthiness check — that would silently drop the zero case.
  const badgeCount = option.badge_count
  const hasBadge = typeof badgeCount === "number"

  // The zero string is selected here rather than left to the i18n layer.
  //
  // ⚠️ MEASURED, because the obvious rationale for this ternary is FALSE and
  // was believed on this ticket until someone ran it. i18next honours a
  // `_zero` sibling key NATIVELY, ahead of CLDR: with the bundle loaded,
  // `t(base, { count: 0 })` returns "Aucun·e créateur·rice" in FR and
  // "No creators" in EN — even though `Intl.PluralRules("fr").select(0)` is
  // `"one"`. So this ternary does NOT rescue us from a wrong plural form;
  // dropping it produces byte-identical output in both locales today. It is
  // an equivalent mutant, and any test claiming to kill it is measuring
  // nothing.
  //
  // It is kept for a narrower, real reason: it makes the rendered string
  // independent of i18next's `_zero` support, which is a library behaviour we
  // do not control and which no test of ours would notice changing. Keep or
  // remove on that argument — not on the CLDR one, which does not survive
  // measurement.
  //
  // ⚠️ Rails is a DIFFERENT story and the asymmetry is load-bearing for specs:
  // `I18n.t(base, count: n)` is MISSING at every count in both locales,
  // because the flat suffix keys leave no `badge_count` node to descend into.
  // `spec/i18n/oks958_badge_count_keys_spec.rb` must therefore assert the
  // three suffixed keys directly; asserting the base key there tests nothing.
  const badgeLabel = hasBadge
    ? badgeCount === 0
      ? t("missions.assign.mode_picker.badge_count_zero")
      : t("missions.assign.mode_picker.badge_count", { count: badgeCount })
    : null

  // 🔴 Computed, never a literal. A static `"${id}-helper ${id}-badge"` would
  // dangle on segment/handpick, which render no badge — and per the
  // `aria-labelledby` warning below, a dangling idref degrades SILENTLY.
  // The description must be built from the same condition that gates the
  // badge render, so the two can never disagree.
  const describedBy = hasBadge ? `${id}-helper ${id}-badge` : `${id}-helper`

  return (
    <label
      htmlFor={id}
      data-testid={`mission-assign-mode-card-${option.value}`}
      data-selected={selected ? "true" : "false"}
      // Mirrors the inner radio's aria-checked so a11y testing can read
      // it from the card-level testid (QA Plan §E11). The actual radio
      // remains the source of truth (Radix RadioGroup owns it); the
      // label simply echoes for tooling.
      aria-checked={selected ? "true" : "false"}
      className={cn(
        "relative flex cursor-pointer flex-col gap-1 rounded-lg border-2 p-4 transition-colors",
        selected
          ? "border-primary bg-primary/5 ring-primary/30 ring-2"
          : "border-input bg-background hover:bg-accent",
        disabled && "cursor-not-allowed opacity-60",
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span
          id={`${id}-title`}
          className="text-foreground text-sm font-semibold"
        >
          {t(`missions.assign.mode.${option.value}.title`)}
        </span>
        {/*
          ⚠️ `aria-labelledby` is REQUIRED here, and it must point at a REAL id.

          The whole card is the `<label>` (click-anywhere + the ≥44px touch
          target), so Base UI's native-label fallback would otherwise pull the
          card's ENTIRE text — title, helper AND the badge count — into the
          accessible name, while `aria-describedby` simultaneously declares the
          helper to be the description. The same sentence would sit in the tree
          twice.

          Pointing `aria-labelledby` at the title span narrows the name to the
          title and leaves the helper as the description. Measured: an explicit
          `aria-labelledby` REPLACES Base UI's generated `${id}-label` (it is
          not appended to it).

          🔴 If this id ever stops resolving — renamed, typo'd, span removed —
          the name silently becomes EMPTY. It does NOT fall back to the label.
          `mission-assign-mode-picker.test.tsx` asserts the full accessible name
          for exactly this reason; keep those assertions if you touch this markup.
        */}
        <RadioGroupItem
          id={id}
          value={option.value}
          disabled={disabled}
          aria-labelledby={`${id}-title`}
          aria-describedby={describedBy}
        />
      </div>
      <p
        id={`${id}-helper`}
        className="text-muted-foreground text-xs leading-tight"
      >
        {t(`missions.assign.mode.${option.value}.helper`)}
      </p>
      {hasBadge && (
        // 🔴 The count lives in its OWN element, never inside the helper <p>.
        // If it were folded into the helper, `helper.textContent` would grow
        // to include it and any DOM-derived assertion would go green having
        // measured nothing — the expectation would move with the defect.
        // The id below is what carries it into the description; the order of
        // the idrefs in `aria-describedby` (helper, then badge) is the
        // contract, and only a full-string assertion in that order reds on a
        // reversal, since both ids still resolve either way.
        <span
          id={`${id}-badge`}
          className="text-muted-foreground text-xs font-medium"
          data-testid={`mission-assign-mode-badge-${option.value}`}
        >
          {badgeLabel}
        </span>
      )}
    </label>
  )
}
